import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { appleEase } from "@/lib/motion";
import SceneVivante from "@/components/home/SceneVivante";

const CSS = `
  .tg-asset-parallax-section {
    position: relative;
    width: 100%;
    padding-top: 140px;
    /* Gris de fond standard du site. La card de SceneVivante est en blanc
       opaque par-dessus. Isolé des sections voisines par les bandes blanches
       de Home.jsx, donc pas de couture. */
    background: #F5F5F7;
    overflow-x: hidden;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  /* Editorial copy stays free on the left; the animation lives large on the
     right and is allowed to breathe past a strict column edge. Not a grid. */
  .tg-asset-hero-row {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 60px;
    box-sizing: border-box;
  }

  .tg-asset-caption {
    position: relative;
    z-index: 2;
    align-self: flex-start;
    width: calc(100% - clamp(20px, 8vw, 110px));
    max-width: 700px;
    margin-left: clamp(20px, 8vw, 110px);
    padding-top: 24px;
    color: #1D1D1F;
    text-align: left;
  }

  .tg-asset-flow-wrap {
    position: relative;
    width: 100%;
    margin-top: 140px;
  }

  /* Label de section. Le titre "Créez une fois. Vendez en continu." est une
     promesse, pas une categorie : le label dit de quoi la section parle. */
  .tg-asset-eyebrow {
    display: block;
    font-size: clamp(18px, 2vw, 19.5px);
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
    text-transform: none;
    /* Couleur unique des labels de l'accueil : un noir adouci, un cran plus
       clair que le #1D1D1F des titres. */
    color: #3A3A3C;
    margin: 0 0 16px;
  }

  .tg-asset-title-text {
    /* Desktop agrandi : plafond monté de 2.5rem → 3.4rem (le mobile garde son
       override 2.2rem dans le @media max-width:768px). */
    font-size: clamp(1.6rem, 4.2vw, 3.4rem);
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.03em;
    margin: 0;
  }

  .tg-asset-dark {
    font-weight: 400;
    color: #000000;
  }

  .tg-asset-parallax-content {
    position: relative;
    z-index: 10;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tg-asset-view-block {
    min-height: 80vh; 
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start; 
    text-align: left; 
    width: 100%;
    max-width: 1200px;
    padding: 0 60px; 
  }

  /* ── Value statement, big and centered right under the interactive card ── */
  .tg-value-title {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding-top: 70px;
    font-size: clamp(2rem, 4vw, 3.2rem);
    font-weight: 400;
    letter-spacing: -0.02em;
    text-align: center;
    background: linear-gradient(90deg, #4B5563 0%, #000000 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: #000000;
  }

  /* ── Apple M5 Style Commission — logo beside the percentages, centered as a group ── */
  .tg-commission-wrap {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 32px;
    width: 100%;
    max-width: 900px;
    padding: 0;
    margin: 100px auto 60px;
  }

  .tg-commission-logo-box {
    flex-shrink: 0;
    width: 110px;
    height: 110px;
    border: 3.5px solid #000000;
    border-radius: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: transparent;
  }

  .tg-commission-logo-text {
    font-size: 18px;
    font-weight: 700;
    color: #000000;
    letter-spacing: -0.01em;
    line-height: 1;
  }

  .tg-commission-divider {
    width: 1px;
    height: 110px;
    background: rgba(0, 0, 0, 0.15);
    flex-shrink: 0;
  }

  .tg-commission-grid {
    display: flex;
    justify-content: center;
    gap: 48px;
    flex-wrap: wrap;
  }

  .tg-commission-item {
    display: flex;
    flex-direction: column;
    max-width: 200px;
  }

  .tg-commission-pct {
    font-size: 52px;
    font-weight: 600;
    letter-spacing: -0.03em;
    color: #000000;
    line-height: 0.9;
    margin-bottom: 12px;
  }

  .tg-commission-label {
    font-size: 12px;
    font-weight: 600;
    color: #424245;
    line-height: 1.4;
  }

  /* ── Icon row — bigger, black, no border, side by side ── */
  .tg-asset-micro-cards {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 64px;
    margin-top: 96px;
    width: 100%;
  }

  .tg-micro-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
    background: transparent;
    border: none;
  }

  .tg-micro-icon {
    color: #000000;
    flex-shrink: 0;
  }

  .tg-micro-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tg-micro-label {
    font-size: 17.5px;
    font-weight: 600;
    color: #000000;
    text-transform: none;
    letter-spacing: -0.01em;
  }

  .tg-micro-text {
    font-size: 14px;
    font-weight: 500;
    color: #424245;
  }

  .tg-asset-discover-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 28px;
    background: transparent;
    color: #1D5FD1;
    border: 1px solid #1D5FD1;
    border-radius: 99px;
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
  }

  .tg-asset-discover-btn:hover {
    background: #F0F5FE;
    transform: scale(1.02);
  }

  @media (max-width: 1024px) {
    .tg-commission-wrap { flex-direction: column; gap: 24px; }
    .tg-commission-divider { display: none; }
    .tg-commission-grid { flex-wrap: wrap; gap: 40px; justify-content: center; }
    .tg-commission-item { align-items: center; text-align: center; }
    .tg-asset-view-block { padding: 0 40px; }
    .tg-asset-hero-row { flex-direction: column; padding: 80px 40px 0; }
    .tg-asset-caption { flex: none; max-width: 100%; padding-top: 0; margin-bottom: 24px; }
    .tg-asset-flow-wrap { margin-left: 0; width: 100%; }
  }

  @media (max-width: 768px) {
    .tg-asset-hero-row { padding: 48px 24px 0; }
    /* Les 140 px du desktop separent deux colonnes cote a cote ; empilee
       sous le titre, la scene se retrouvait decrochee de lui. */
    .tg-asset-flow-wrap { margin-top: 40px; }
    .tg-asset-title-text { font-size: 2.2rem; }
    .tg-asset-view-block { padding: 0 24px; min-height: 60vh; }
    .tg-commission-pct { font-size: 42px; }
    .tg-value-title { padding-top: 100px; }
    .tg-asset-micro-cards { margin-top: 56px; gap: 40px; }
  }
`;

export default function AssetSection() {
  const reveal = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 1, ease: appleEase }
  };

  return (
    <>
      <style>{CSS}</style>
      <section className="tg-asset-parallax-section">
        <div className="tg-asset-hero-row">
          <div className="tg-asset-caption">
            <span className="tg-asset-eyebrow">L'actif</span>
            <h2 className="tg-asset-title-text">
              Créez une fois.<br /><span className="tg-asset-dark">Vendez en continu.</span>
            </h2>
          </div>

          <div className="tg-asset-flow-wrap">
            <SceneVivante />
          </div>
        </div>

        <div className="tg-asset-parallax-content" style={{ marginTop: '20px' }}>
            {/* 2. Value statement, Commission & Micro Cards */}
            <div className="tg-asset-view-block" style={{ height: 'auto', marginBottom: '120px' }}>
              <motion.h2 className="tg-value-title" {...reveal}>
                La valeur est partagée.
              </motion.h2>

              <motion.div className="tg-commission-wrap" {...reveal}>
                <div className="tg-commission-logo-box">
                  <span className="tg-commission-logo-text">TANGIG</span>
                </div>

                <div className="tg-commission-divider" />

                <div className="tg-commission-grid">
                  <div className="tg-commission-item">
                    <span className="tg-commission-pct">80%</span>
                    <p className="tg-commission-label">de revenus directs pour le créateur de l'actif.</p>
                  </div>

                  <div className="tg-commission-item">
                    <span className="tg-commission-pct">10%</span>
                    <p className="tg-commission-label">pour le réseau d'affiliés qui diffuse votre travail.</p>
                  </div>

                  <div className="tg-commission-item">
                    <span className="tg-commission-pct">10%</span>
                    <p className="tg-commission-label">pour l'infrastructure et la sécurité TANGIG.</p>
                  </div>
                </div>
              </motion.div>

              <div className="tg-asset-micro-cards">
                <motion.div className="tg-micro-card" {...reveal} transition={{ delay: 0.4 }}>
                  <svg className="tg-micro-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  <div className="tg-micro-content">
                    <span className="tg-micro-label">Paiement</span>
                    <span className="tg-micro-text">14 jours de sécurisation</span>
                  </div>
                </motion.div>

                <motion.div className="tg-micro-card" {...reveal} transition={{ delay: 0.5 }}>
                  <svg className="tg-micro-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <div className="tg-micro-content">
                    <span className="tg-micro-label">Prix minimum</span>
                    <span className="tg-micro-text">99€, puis libre</span>
                  </div>
                </motion.div>

                <motion.div className="tg-micro-card" {...reveal} transition={{ delay: 0.6 }}>
                  <svg className="tg-micro-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <div className="tg-micro-content">
                    <span className="tg-micro-label">Traitement</span>
                    <span className="tg-micro-text">Stripe sécurisé</span>
                  </div>
                </motion.div>
              </div>

              <motion.div
                {...reveal}
                transition={{ ...reveal.transition, delay: 0.7 }}
                style={{ width: '100%', textAlign: 'center', marginTop: '80px' }}
              >
                <Link to="/marketplace" className="tg-asset-discover-btn">
                  Découvrir
                </Link>
              </motion.div>
            </div>
        </div>
      </section>
    </>
  );
}
